import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import { t } from "i18next";
import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import { FlatList, Text, Pressable, View } from "react-native";

interface InputChooseProps {
  label: string;
  data: any;
  control?: any;
  name: string;
  rules?: object;
  handleChange?: (text: string) => void;
  handleSelectedItem?: (data: { value: string; code: string }) => void;
  optional?: boolean;
  blocked?: boolean;
  isMultipleSelection?: boolean;
  selectionArray?: { value: string; code: string }[];
  deleteItem?: (value: string, code: string) => void;
}

interface ListItem {
  displayValue: string;
  internalValue: string;
}

export default function InputChoose(props: InputChooseProps) {
  const [selectedValue, setSelectedValue] = useState("");
  const [isListVisible, setIsListVisible] = useState(false);
  const [convertedData, setConvertedData] = useState(props.data);
  const [hoverStates, setHoverStates] = useState({});
  const [isListFocused, setIsListFocused] = useState(false);

  const verifyLength = Object.keys(props.data).length;
  if (verifyLength !== 1) {
    throw new Error("You forgot a {} in props.data declaration");
  }

  useEffect(() => {
    const name = Object.keys(props.data)[0];
    if (props.data && !Array.isArray(props.data)) {
      const newData = Object.entries(props.data[name]).map(([key, value]) => ({
        displayValue: t("enums:" + name + "." + value),
        internalValue: value,
      }));
      setConvertedData(newData);
    } else {
      setConvertedData(props.data);
    }
  }, [props.data]);

  useEffect(() => {
    if (props.blocked) {
      setIsListVisible(false);
    }
  }, [props.blocked]);

  const handleSelect = (item: ListItem, onChange: (value: string) => void) => {
    if (props.isMultipleSelection) {
      props.handleSelectedItem?.({
        value: item.displayValue,
        code: item.internalValue,
      });
      setSelectedValue("");
      setIsListVisible(false);
      setIsListFocused(false);
      setHoverStates({});
      return;
    }
    setSelectedValue(item.displayValue);
    onChange(item.internalValue);
    setHoverStates((prev) => {
      const resetHoverStates = {};
      for (const key in prev) {
        resetHoverStates[key] = false;
      }
      return resetHoverStates;
    });
    if (props.handleChange) {
      props.handleChange(item.internalValue);
    }
    setIsListVisible(false);
    setIsListFocused(false);
  };

  const handleHover = (itemKey, isHovered) => {
    setHoverStates((prev) => ({ ...prev, [itemKey]: isHovered }));
  };

  const renderItem = ({ item, onChange }) => {
    const itemKey = item.displayValue;
    const isOptionAlreadySelected = props.selectionArray?.some(
      (selectedValue) => selectedValue.value === item.displayValue,
    );

    return !isOptionAlreadySelected ? (
      <Pressable
        onPress={() => handleSelect(item, onChange)}
        onHoverIn={() => handleHover(itemKey, true)}
        onHoverOut={() => handleHover(itemKey, false)}
        className={`p-2.5 ${hoverStates[itemKey] ? "bg-gray" : "bg-white1"}`}
      >
        <View>
          <Text className="text-sm">{item.displayValue}</Text>
        </View>
      </Pressable>
    ) : null;
  };

  return (
    <View>
      {props.optional === true ? (
        <View className="flex-row m-2">
          <Text numberOfLines={undefined} className="text-black text-sm">
            {props.label}
          </Text>
          <Text className="text-midgray text-sm italic">{` (${t(
            "common:placeholder.optional",
          )})`}</Text>
        </View>
      ) : (
        <Text
          numberOfLines={undefined}
          className={`${
            props.blocked ? "text-midgray" : "text-black"
          } text-sm m-2`}
        >
          {props.label}
        </Text>
      )}
      <Controller
        control={props.control}
        name={props.name}
        rules={props.rules}
        defaultValue=""
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <View>
              <Pressable
                ref={ref}
                onPress={() => {
                  if (props.blocked) {
                    setIsListVisible(false);
                  } else {
                    setIsListVisible(!isListVisible);
                  }
                }}
                onBlur={() => {
                  if (!isListFocused) {
                    setIsListVisible(false);
                  }
                }}
              >
                <View
                  className={`flex-row justify-between items-center border-solid border w-80 h-14 py-6 px-4 ${
                    error ? "border-red" : "border-midgray"
                  } ${
                    isListVisible
                      ? " rounded-b-none border-b-0 rounded-tr-xl rounded-tl-xl"
                      : " rounded-xl"
                  }`}
                >
                  <Text
                    className={`text-sm ${
                      selectedValue ? "text-black1" : "text-midgray"
                    }`}
                  >
                    {`${selectedValue || value ? selectedValue : ""}` ||
                      t("common:placeholder.choose")}
                  </Text>
                  <MaterialIcons
                    name="arrow-drop-down"
                    size={24}
                    color="#BDBDBD"
                    className="absolute top-4 right-3"
                  />
                </View>
              </Pressable>
              {props.selectionArray && props.selectionArray.length > 0 && (
                <View className="flex-row flex-wrap items-start p-2 max-w-80 h-fit">
                  {props.selectionArray.map((selectedValue, index) => (
                    <View
                      className="bg-purple5 flex-row p-2 items-center rounded-lg mt-0.5 ml-0.5 mr-0.5 max-w-24"
                      key={index}
                    >
                      <Text
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        className="text-black text-xs text-white1 mr-1"
                      >
                        {selectedValue.value}
                      </Text>
                      <Pressable
                        className="items-center"
                        onPress={() =>
                          props.deleteItem?.(
                            selectedValue.value,
                            selectedValue.code,
                          )
                        }
                      >
                        <AntDesign name="close" color="#FFFFFF" size={10} />
                      </Pressable>
                    </View>
                  ))}
                </View>
              )}
              <View style={{ height: 20, overflow: "hidden" }}>
                {error && (
                  <Text className="text-red text-xs pl-2">{error.message}</Text>
                )}
              </View>
              {isListVisible && (
                <FlatList
                  data={convertedData}
                  onStartReached={() => setIsListFocused(true)}
                  renderItem={({ item }) => renderItem({ item, onChange })}
                  keyExtractor={(item) => item.displayValue}
                  className={`absolute top-14 w-80 max-h-32 bg-white1 border border-midgray rounded-bl-xl ${
                    convertedData.length <= 3 ? "rounded-br-xl" : ""
                  }`}
                />
              )}
            </View>
          </>
        )}
      />
    </View>
  );
}
